import styled from 'styled-components';
import { ActiveAuctions, useGetActiveAuctionsQuery, useShowAllTabQuery } from 'services/api/live-auction';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useSearchParams, useUpdateSearchParams } from 'hooks';

type AuctionTabsProps = {
  selectedTab: string;
  selectTab: (tab: string) => void;
  fontSize?: 'md' | 'lg';
  paddings?: 'md' | 'lg';
  hideNonActive?: boolean;
  containerStyles?: any;
  preserveTabAfterNav?: boolean;
  includeAllTab?: boolean;
};

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
`;

const TabButton = styled.button<{ selected: boolean; fontSize: 'md' | 'lg'; paddings: 'md' | 'lg' }>`
  flex: 1;
  min-width: fit-content;
  background: ${({ selected }) => (selected ? '#f3f3f3' : 'transparent')};
  color: ${({ selected }) => (selected ? '#225a91' : '#666666')};
  font-size: ${({ fontSize }) => (fontSize === 'lg' ? '16px' : '12px')};
  font-weight: ${({ selected }) => (selected ? '500' : '400')};
  line-height: 100%;
  text-transform: uppercase;
  border: none;
  max-width: 140px;
  outline: none;
  cursor: pointer;
  padding: ${({ paddings }) => (paddings === 'lg' ? '26px 32px' : '26px 12px')};
  border-radius: 0;
  position: relative;
  transition: color 0.2s, font-weight 0.2s, background 0.2s;
  &:hover {
    color: #225a91;
    font-weight: 500;
    background: #f3f3f3;
  }
  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 4px;
    background: ${({ selected }) => (selected ? '#3994DE' : 'transparent')};
    bottom: 4px;
    border-radius: 2px;
    transition: background 0.2s;
  }
  &:hover::after {
    background: #3994de;
  }
`;

const tabs = [
  {
    label: 'Autoaxess',
    value: 'autoaxess'
  },
  {
    label: 'Second Chance',
    value: 'second_chance'
  },
  {
    label: 'Frontline',
    value: 'frontline'
  }
];

const selectDefaultValue = (auctions: ActiveAuctions, location: string, showAllTab: boolean) => {
  const entries = Object.entries(auctions);
  if (entries.length === 0) return 'autoaxess';
  if (showAllTab) return 'all';
  if (location === '/live-auction') {
    for (const [key, auction] of entries) {
      if (!auction?.counters) continue;
      if (auction.counters.liveCount > 0) return key;
    }

    return entries[0][0];
  }

  const filteredEntries = entries.filter(([, value]) => !!value?.counters?.myCount && value?.counters?.myCount > 0);
  if (filteredEntries.length === 0) return 'autoaxess';

  if (location === '/buyer/bids' || location === '/seller/dashboard') {
    for (const [key, auction] of filteredEntries) {
      if (!auction?.counters) continue;
      if (auction.counters.myActiveCount > 0) return key;
    }

    return filteredEntries.at(-1)![0];
  }

  return filteredEntries[0][0];
};

export const AuctionTabs = ({
  selectedTab,
  selectTab: originalSelectTab,
  fontSize = 'md',
  paddings = 'lg',
  hideNonActive = false,
  containerStyles = {},
  preserveTabAfterNav = false,
  includeAllTab = false
}: AuctionTabsProps) => {
  const { data: activeAuctions } = useGetActiveAuctionsQuery(undefined, { skip: !hideNonActive });

  const { data: showAllTabResponse } = useShowAllTabQuery(undefined, { skip: !includeAllTab });

  const showAllTab = !!showAllTabResponse?.shouldShow;

  const [shouldRender, setShouldRender] = useState(!hideNonActive);

  const qs = useSearchParams();
  const updateQs = useUpdateSearchParams();

  const location = useLocation();

  const selectTab = (tab: string) => {
    if (preserveTabAfterNav) {
      updateQs({ auction_type: tab });
    }

    originalSelectTab(tab);
  };

  useEffect(() => {
    if (!activeAuctions) return;

    if (qs.has('auction_type') && preserveTabAfterNav) {
      if (selectedTab !== qs.get('auction_type')) {
        selectTab(qs.get('auction_type')!);
      }

      return;
    }

    const defaultTab = selectDefaultValue(activeAuctions, location.pathname, showAllTab);
    selectTab(defaultTab);
  }, [location.pathname, activeAuctions, showAllTab]);

  const filteredTabs = useMemo(() => {
    if (!activeAuctions || !hideNonActive) return tabs;

    const filtered = tabs.filter(tab => {
      const activeAuction = activeAuctions[tab.value as keyof typeof activeAuctions];
      if (!activeAuction) return false;

      if (location.pathname === '/buyer/bids' || location.pathname === '/seller/dashboard') {
        return activeAuction.counters.myCount > 0;
      }

      return true;
    });

    if (showAllTab) {
      filtered.unshift({ value: 'all', label: 'All' });
    }

    console.log(selectedTab, 'selectedTab');
    const parsed = filtered.length > 0 ? filtered : [tabs[0]];
    if (!parsed.some(t => t.value === selectedTab)) {
      selectTab(parsed[0].value);
    }
    setShouldRender(true);
    return parsed;
  }, [activeAuctions, hideNonActive, location.pathname]);

  if (!shouldRender) return null;

  return (
    <TabsContainer style={containerStyles}>
      {filteredTabs.map(tab => (
        <TabButton
          paddings={paddings}
          className="auction-tab"
          key={tab.value}
          selected={selectedTab === tab.value}
          onClick={() => selectTab(tab.value)}
          fontSize={fontSize}
        >
          {tab.label}
        </TabButton>
      ))}
    </TabsContainer>
  );
};
